import React, { memo } from 'react';
import {
  Dialog,
  DialogContent,
  Typography,
  Button,
  IconButton,
  Box,
  Link,
} from '@mui/material';
import { Sparkles, Mic2, MapPin, Mail, Code2, Heart, X } from 'lucide-react';

const AboutDialog = ({ show, close }) => {
  return (
    <Dialog
      open={show}
      onClose={close}
      maxWidth="sm"
      fullWidth
      slotProps={{
        backdrop: {
          sx: {
            background: 'rgba(6, 1, 14, 0.85)',
            backdropFilter: 'blur(12px)',
            WebkitBackdropFilter: 'blur(12px)',
          },
        },
      }}
      PaperProps={{
        sx: {
          width: '94vw',
          maxWidth: '540px',
          borderRadius: '22px',
          background: '#0f081d !important',
          backgroundColor: '#0f081d !important',
          backdropFilter: 'none !important',
          WebkitBackdropFilter: 'none !important',
          border: '1px solid rgba(0, 242, 254, 0.35)',
          boxShadow: '0 25px 70px rgba(0, 0, 0, 0.98), 0 0 35px rgba(0, 242, 254, 0.25)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          m: 'auto',
        },
      }}
    >
      {/* Header del Modal */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: { xs: 2.5, sm: 3 },
          py: 2.2,
          mb: 2,
          borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
          background: '#150a26',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '12px',
              background: 'linear-gradient(135deg, rgba(0, 242, 254, 0.2) 0%, rgba(180, 77, 255, 0.2) 100%)',
              border: '1px solid rgba(0, 242, 254, 0.45)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#00f2fe',
              boxShadow: '0 0 15px rgba(0, 242, 254, 0.3)',
            }}
          >
            <Mic2 size={22} />
          </Box>
          <Box>
            <Typography
              variant="h6"
              sx={{
                fontSize: { xs: '1.1rem', sm: '1.25rem' },
                fontWeight: 700,
                color: '#ffffff',
                lineHeight: 1.2,
                letterSpacing: '-0.02em',
              }}
            >
              Acerca de Garaoke
            </Typography>
            <Typography sx={{ fontSize: '0.8rem', color: 'rgba(255, 255, 255, 0.55)', mt: 0.3 }}>
              Karaoke Online Interactivo · Edición Gamer
            </Typography>
          </Box>
        </Box>

        <IconButton
          aria-label="close"
          onClick={close}
          size="small"
          sx={{
            color: 'rgba(255, 255, 255, 0.65)',
            background: 'rgba(255, 255, 255, 0.04)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            borderRadius: '10px',
            p: '7px',
            transition: 'all 0.2s ease',
            '&:hover': {
              color: '#ffffff',
              background: 'rgba(0, 242, 254, 0.2)',
              borderColor: 'rgba(0, 242, 254, 0.5)',
              transform: 'scale(1.05)',
            },
          }}
        >
          <X size={18} />
        </IconButton>
      </Box>

      {/* Cuerpo del Modal */}
      <DialogContent
        sx={{
          p: { xs: 2.5, sm: 3 },
          display: 'flex',
          flexDirection: 'column',
          gap: 2.5,
        }}
      >
        {/* Tarjeta de presentación / Misión */}
        <Box
          sx={{
            p: 2.2,
            borderRadius: '16px',
            background: 'linear-gradient(135deg, rgba(255, 45, 155, 0.08) 0%, rgba(0, 242, 254, 0.06) 100%)',
            border: '1px solid rgba(255, 45, 155, 0.2)',
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#ff2d9b' }}>
            <Sparkles size={16} />
            <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Misión del Proyecto
            </Typography>
          </Box>
          <Typography sx={{ fontSize: '0.92rem', color: 'rgba(255, 255, 255, 0.9)', lineHeight: 1.55 }}>
            Este proyecto fue creado para llevar la <strong>máxima diversión</strong> a tu fiesta de un modo simple e intuitivo, sin instalaciones complicadas y <strong>100% GRATIS</strong> para todos los amantes del Karaoke.
          </Typography>
        </Box>

        {/* Grilla de Datos Técnicos y Origen */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
            gap: 1.5,
          }}
        >
          {/* Versión */}
          <Box
            sx={{
              p: 1.8,
              borderRadius: '14px',
              background: 'rgba(255, 255, 255, 0.03)',
              border: '1px solid rgba(255, 255, 255, 0.08)',
              display: 'flex',
              flexDirection: 'column',
              gap: 0.5,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#00f2fe' }}>
              <Code2 size={16} />
              <Typography sx={{ fontSize: '0.78rem', fontWeight: 600, color: 'rgba(255, 255, 255, 0.6)' }}>
                Versión
              </Typography>
            </Box>
            <Typography sx={{ fontSize: '0.95rem', fontWeight: 700, color: '#ffffff' }}>
              v2.0.0 <span style={{ fontSize: '0.78rem', color: '#00f2fe', fontWeight: 500 }}>(Gamer Edition)</span>
            </Typography>
          </Box>

          {/* Origen */}
          <Box
            sx={{
              p: 1.8,
              borderRadius: '14px',
              background: 'rgba(255, 255, 255, 0.03)',
              border: '1px solid rgba(255, 255, 255, 0.08)',
              display: 'flex',
              flexDirection: 'column',
              gap: 0.5,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#ff2d9b' }}>
              <MapPin size={16} />
              <Typography sx={{ fontSize: '0.78rem', fontWeight: 600, color: 'rgba(255, 255, 255, 0.6)' }}>
                Origen
              </Typography>
            </Box>
            <Typography sx={{ fontSize: '0.92rem', fontWeight: 600, color: '#ffffff' }}>
              Chacabuco, Buenos Aires, Arg.
            </Typography>
          </Box>

          {/* Creador */}
          <Box
            sx={{
              p: 1.8,
              borderRadius: '14px',
              background: 'rgba(255, 255, 255, 0.03)',
              border: '1px solid rgba(255, 255, 255, 0.08)',
              display: 'flex',
              flexDirection: 'column',
              gap: 0.5,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#b44dff' }}>
              <Heart size={16} />
              <Typography sx={{ fontSize: '0.78rem', fontWeight: 600, color: 'rgba(255, 255, 255, 0.6)' }}>
                Desarrollado por
              </Typography>
            </Box>
            <Typography sx={{ fontSize: '0.92rem', fontWeight: 600, color: '#ffffff' }}>
              Gabriel Tonelli
            </Typography>
          </Box>

          {/* Contacto / Email */}
          <Box
            sx={{
              p: 1.8,
              borderRadius: '14px',
              background: 'rgba(255, 255, 255, 0.03)',
              border: '1px solid rgba(255, 255, 255, 0.08)',
              display: 'flex',
              flexDirection: 'column',
              gap: 0.5,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#00f2fe' }}>
              <Mail size={16} />
              <Typography sx={{ fontSize: '0.78rem', fontWeight: 600, color: 'rgba(255, 255, 255, 0.6)' }}>
                Contacto directo
              </Typography>
            </Box>
            <Link
              href="mailto:gabrieltonelli@gmail.com"
              sx={{
                fontSize: '0.85rem',
                color: '#00f2fe',
                textDecoration: 'none',
                wordBreak: 'break-all',
                fontWeight: 500,
                '&:hover': { textDecoration: 'underline' },
              }}
            >
              gabrieltonelli@gmail.com
            </Link>
          </Box>
        </Box>
      </DialogContent>

      {/* Footer del Modal */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          px: { xs: 2.5, sm: 3 },
          py: 2,
          borderTop: '1px solid rgba(255, 255, 255, 0.08)',
          background: 'rgba(255, 255, 255, 0.02)',
        }}
      >
        <Button
          variant="contained"
          onClick={close}
          sx={{
            textTransform: 'none',
            borderRadius: '10px',
            background: 'linear-gradient(135deg, #00f2fe 0%, #4facfe 100%)',
            color: '#05070f',
            fontWeight: 700,
            fontSize: '0.88rem',
            px: 3.5,
            py: 0.8,
            boxShadow: '0 4px 18px rgba(0, 242, 254, 0.4)',
            transition: 'all 0.2s ease',
            '&:hover': {
              background: 'linear-gradient(135deg, #33f5fe 0%, #68b8fe 100%)',
              boxShadow: '0 6px 24px rgba(0, 242, 254, 0.65)',
              transform: 'scale(1.02)',
            },
            '&:active': {
              transform: 'scale(0.98)',
            },
          }}
        >
          ¡A cantar!
        </Button>
      </Box>
    </Dialog>
  );
};

export default memo(AboutDialog);
